import { HTMLAttributes } from 'react';
import styled from 'styled-components';
import { Material } from '/types/jobTemplateTypes';
import formatPrice from '/lib/utils/formatPrice';
import { colors, effects } from '/css';
import Icon from '/components/ui/Icon';

interface Props extends HTMLAttributes<HTMLElement> {
    as?: any;
    material: Material;
    onImageClick: (material: Material) => void;
    withTitle?: boolean;
    hidePrice?: boolean;
    hideQuantity?: boolean;
    withIncludedProductCheckbox?: boolean;
    withIncludedOnReceiptCheckbox?: boolean;
    onIncludedOnReceiptToggle?: (articleNo: string) => void;
    isIncludedOnReceipt?: boolean;
    readOnly?: boolean;
    removeable?: boolean;
    onRemove?: (material: Material) => void;
}

interface EditableProps extends Props {
    onChangePrice: (val: string) => void;
    onChangeQuantity: (val: string) => void;
    readOnly?: false | never | undefined;
    onToggleIsProduct: (material: Material) => void;
    hideActions?: boolean;
}

interface ReadOnlyProps extends Props {
    readOnly: true;
    onChangePrice?: never | undefined;
    onChangeQuantity?: never | undefined;
    onToggleIsProduct?: never | undefined;
    hideActions?: never | undefined;
}

const MaterialListItem = ({
    material,
    onImageClick,
    withTitle,
    hidePrice,
    readOnly,
    onChangePrice,
    onChangeQuantity,
    onToggleIsProduct,
    onRemove,
    hideActions,
    hideQuantity,
    removeable,
    withIncludedProductCheckbox,
    withIncludedOnReceiptCheckbox,
    onIncludedOnReceiptToggle,
    isIncludedOnReceipt,
    ...props
}: EditableProps | ReadOnlyProps) => (
    <Component {...props}>
        <span className="img">
            {Array.isArray(material.picture) && material.picture[0] && (
                <img
                    src={material.picture[0].url || material.picture[0].uRL}
                    onClick={() => onImageClick(material)}
                />
            )}
        </span>
        <span className="code">{material.articleNo}</span>
        <span className="name">{withTitle ? material.name : ''}</span>
        {!hidePrice && (
            <span className="price">
                {formatPrice(
                    Number(
                        readOnly && material.price?.spotonPrice
                            ? material.price.spotonPrice
                            : material.price?.price
                    )
                )}
            </span>
        )}
        {!readOnly && !hidePrice && (
            <span className="price input">
                <small>Kr</small>
                <input
                    value={material.price?.spotonPrice}
                    onChange={(e) =>
                        onChangePrice ? onChangePrice(e.target.value) : null
                    }
                    onFocus={(e) => e.target.select()}
                    onBlur={() => {
                        if (!material.price?.spotonPrice) {
                            onChangePrice(String(material.price?.price));
                        }
                    }}
                />
            </span>
        )}
        {!hideQuantity && (
            <span className="qty">
                {!readOnly ? (
                    <input
                        value={material.quantity}
                        onChange={(e) => onChangeQuantity(e.target.value)}
                        onFocus={(e) => e.target.select()}
                    />
                ) : (
                    material.quantity
                )}
            </span>
        )}

        {withIncludedOnReceiptCheckbox && onIncludedOnReceiptToggle && (
            <span className="receipt">
                <input
                    type="checkbox"
                    className="checkbox new-style"
                    checked={isIncludedOnReceipt}
                    onChange={() =>
                        onIncludedOnReceiptToggle(material.articleNo)
                    }
                />
            </span>
        )}

        {withIncludedProductCheckbox && onToggleIsProduct && (
            <span className="product">
                <input
                    type="checkbox"
                    className="checkbox new-style"
                    checked={!!material.isProduct}
                    onChange={() => onToggleIsProduct(material)}
                />
            </span>
        )}
        {(removeable || (!hideActions && !readOnly)) && onRemove && (
            <span className="actions">
                <Icon
                    icon="empty-field"
                    fill={colors.black}
                    onClick={() => onRemove(material)}
                />
            </span>
        )}
    </Component>
);

const Component = styled.li`
    border-bottom: 3px solid ${colors.grey3};

    &:last-of-type {
        border-bottom: none;
    }
    display: flex;
    padding: 18px 8px;
    position: relative;
    @media (max-width: 767px) {
        flex-wrap: wrap;
    }
    span {
        display: flex;
        align-items: center;
        position: relative;
        input:not([type='checkbox']) {
            border-radius: 12px;
            font-size: 16px;
            padding: 0 24px 2px 24px;
            display: flex;
            align-items: center;
            height: 46px;
            ${effects.shadow};
            border: none;
        }
        &.img {
            min-width: 60px;
            max-width: 60px;
            img {
                display: block;
                cursor: pointer;
            }
        }
        &.code {
            min-width: auto;
            max-width: auto;
            flex: 1 1 auto;
            padding: 0 18px;
            font-family: 'gellix-semi';
        }
        &.name {
            flex: 1 1 auto;
            padding: 0 18px;
            font-family: 'gellix-semi';
        }
        &.receipt {
            width: 90px;
            display: inline-block;
            align-self: center;
            text-align: right;
            padding-left: 20px;
        }
        &.product {
            width: 80px;
            display: inline-block;
            align-self: center;
            text-align: right;
            padding-left: 20px;
        }
        &.price {
            width: 80px;
            min-width: 80px;
            max-width: 80px;
            &.input {
                width: 120px;
                min-width: 120px;
                max-width: 120px;
                small {
                    font-size: 16px;
                    color: ${colors.grey6};
                    position: absolute;
                    top: 50%;
                    left: 12px;
                    transform: translateY(-50%);
                    padding-bottom: 2px;
                }
                input {
                    padding-left: 32px;
                }
            }
        }
        &.qty {
            width: 85px;
            min-width: 85px;
            max-width: 85px;
            padding-left: 18px;
        }
        &.actions {
            width: 30px;
            min-width: 30px;
            max-width: 30px;
            display: flex;
            justify-content: flex-end;
        }
    }
    &.with-title {
        .code {
            min-width: 100px;
            max-width: 100px;
            flex: none;
        }
    }

    @media (max-width: 767px) {
        > span {
            width: 100% !important;
            max-width: 100% !important;
            padding: 0 !important;
            margin-bottom: 8px;
            flex-wrap: wrap;
            &:before {
                content: '-';
                display: block;
                width: 100%;
                margin-bottom: 4px;
                color: ${colors.grey6};
            }
            &.img {
                max-width: 50px !important;
                &:before {
                    display: none;
                }
            }
            &.code:before {
                content: 'Arikkelnr.';
            }
            &.name:before {
                content: 'Arikkelnavn';
            }
            &.qty:before {
                content: 'Kvantum';
            }
            &.price:before {
                content: 'Pris';
            }
            &.price.input small {
                top: 48px;
            }
            &.price.input:before {
                content: 'SpotOn pris';
            }
            &.receipt:before {
                content: 'Kvittering';
            }
            &.receipt {
                text-align: left;
            }
            &.product {
                text-align: left;
            }
            &.product:before {
                content: 'Produkt';
            }
            &.actions {
                margin: 0;
                position: absolute;
                top: 18px;
                right: 8px;
                &:before {
                    display: none;
                }
            }
        }
    }
`;

export default MaterialListItem;
